import { Dictionary } from 'lodash';
import { assign, createMachine } from 'xstate';
import { LocaleCode, MarketCode } from '@tink/config/src/markets';
import _groupBy from 'lodash/groupBy';
import { ProductType } from '@tink/core/src/types';
import {
  AuthenticationUserType,
  FinancialServicesSegment,
  Provider,
  ProviderAccessType,
  ProviderEntity,
} from '../../models/provider';
import { CredentialsType } from '../../models/credentials';
import { getUniqueFinancialServices } from '../screens/FinancialServicePicker/FinancialServicePicker';
import { CommonError } from '../../stores/app-state-errors';
import { ERROR_PROVIDERS } from '../../services/providersService';
import {
  ERR_FETCH_DEMOBANK_SCENARIOS_FAILED,
  ERR_FETCH_REGION_MAPPING,
  ERR_PROVIDER_NOT_FOUND,
  ERR_USER_CANCELLED,
} from '../../utilities/error';
import { ResponseObj } from '../../lib-api/services/base';
import { getProvider, getProviders, ProvidersList } from '../../lib-api/services/Providers';
import { findProvider } from '../../utilities/provider-utils';
import { getNormalizedProviderName } from '../../utilities/provider-view-set';
import { DemoBankScenario } from '../../models/demobankScenarios';
import { demoBankService } from '../../services/demoBankService';

const groupByFinancialInstitution = (providers: ProviderEntity[]): Dictionary<ProviderEntity[]> => {
  const unique = _groupBy(providers, (p: ProviderEntity) => {
    return p.financialInstitutionId;
  });

  return unique;
};

const groupByAuthenticationUserType = (
  providers: ProviderEntity[]
): Dictionary<ProviderEntity[]> => {
  const unique = _groupBy(providers, (p: ProviderEntity) => {
    return p.authenticationUserType;
  });

  return unique;
};

const groupByAccessType = (providers: ProviderEntity[]): Dictionary<ProviderEntity[]> => {
  const unique = _groupBy(providers, (p: ProviderEntity) => {
    return p.accessType;
  });

  return unique;
};

const groupByCredentialsType = (providers: ProviderEntity[]): Dictionary<ProviderEntity[]> => {
  const unique = _groupBy(providers, (p: ProviderEntity) => {
    return p.credentialsType;
  });

  return unique;
};

const groupByDisplayDescription = (providers: ProviderEntity[]): Dictionary<ProviderEntity[]> => {
  const unique = _groupBy(providers, (p: ProviderEntity) => {
    return p.displayDescription;
  });

  return unique;
};

type Events =
  | {
      type: 'SELECT_GROUP_DISPLAY_NAME';
      groupDisplayName: string;
    }
  | {
      type: 'SELECT_FINANCIAL_INSTITUTION';
      financialInstitutionId: string;
    }
  | {
      type: 'USER_CANCELLED';
    }
  | {
      type: 'RESET_FILTERS';
    }
  | {
      type: 'UNSTABLE_PROVIDER_ACKNOWLEDGED';
    }
  | {
      type: 'RESET_FINANCIAL_SERVICES_DATA';
    }
  | {
      type: 'SELECT_AUTHENTICATION_USER_TYPE';
      authenticationUserType: AuthenticationUserType;
    }
  | {
      type: 'SELECT_ACCESS_TYPE';
      accessType: ProviderAccessType;
    }
  | {
      type: 'SELECT_CREDENTIALS_TYPE';
      credentialsType: CredentialsType;
    }
  | {
      type: 'SELECT_FINANCIAL_SERVICE';
      accessType: ProviderAccessType;
      financialServicesSegment: FinancialServicesSegment;
    }
  | {
      type: 'SELECT_PROVIDER_NAME';
      providerName: string;
    }
  | {
      type: 'SELECT_DISPLAY_NAME';
      displayName: string;
    }
  | {
      type: 'SELECT_DEMOBANK_SCENARIO';
      scenarioId: string;
    }
  | {
      type: 'SELECT_NO_SCENARIO';
    }
  | {
      type: 'SELECT_REGION_PICKER';
      regionName: string;
    }
  | {
      type: 'SHOW_REGION_PICKER';
    };

type BranchName = string;
type State = string;

export type Context = {
  providerMissing: boolean;
  error: CommonError | undefined;
  providers: ProviderEntity[];
  filters: {
    credentialsType?: CredentialsType;
    accessType?: string;
    authenticationUserType?: AuthenticationUserType;
    groupDisplayName?: string;
    financialInstitutionId?: string;
    providerName?: string;
    displayName?: string;
    regionName?: string;
  };
  choices: Record<string, ProviderEntity[]>;
  selectedProvider: ProviderEntity | undefined;
  fetchedProvider: ProviderEntity | undefined;
  selectedDemobankScenario: DemoBankScenario | undefined;
  demobankScenarios: DemoBankScenario[];
  regionMappings: Record<BranchName, State>;
};

type Services = {
  fetchSingleProvider: { data: ProviderEntity | undefined };
  findSelectedProvider: { data: ProviderEntity };
  fetchProviderList: { data: ProviderEntity[] };
  fetchDemobankScenarios: { data: DemoBankScenario[] };
  fetchRegionMappings: { data: Record<BranchName, State> };
};

const embeddedDemobankProvider = (p: ProviderEntity) =>
  p.name.endsWith('demobank-password') && p.financialInstitutionName === 'Tink Demo Bank';

export const hasDemobankProviders = (providers: ProviderEntity[]) =>
  providers.some(embeddedDemobankProvider);

export const findDemobankProviders = (providers: ProviderEntity[]) =>
  providers.find(embeddedDemobankProvider);

export const getRegionMappings = async () => {
  const response = await import('./sparkassenBranchStates.json');

  return response.default;
};

type GetFilteredProvidersPayload = {
  filters: Context['filters'];
  providers: ProviderEntity[];
  regionMappings: Record<BranchName, State>;
};

export const getFilteredProviders = ({ filters, providers }: GetFilteredProvidersPayload) => {
  let result = providers;
  const {
    groupDisplayName,
    financialInstitutionId,
    accessType,
    authenticationUserType,
    credentialsType,
    providerName,
    displayName,
  } = filters;

  if (groupDisplayName) {
    result = result.filter((provider) => getNormalizedProviderName(provider) === groupDisplayName);
  }

  if (financialInstitutionId) {
    result = result.filter(
      (provider) => provider.financialInstitutionId === financialInstitutionId
    );
  }

  if (accessType) {
    result = result.filter((provider) => provider.accessType === accessType);
  }

  if (authenticationUserType) {
    result = result.filter((provider) => {
      const authenticationUserTypeMatches =
        provider.authenticationUserType === authenticationUserType;
      const financialServiceFound = provider.financialServices?.find((fs) => {
        return fs.segment === (authenticationUserType as unknown as FinancialServicesSegment);
      });

      return authenticationUserTypeMatches || financialServiceFound;
    });
  }

  if (credentialsType) {
    result = result.filter((provider) => provider.credentialsType === credentialsType);
  }

  if (providerName) {
    result = result.filter((provider) => provider.name === providerName);
  }

  if (displayName) {
    result = result.filter((provider) => provider.displayName === displayName);
  }

  return result;
};

export const resetFilters = (filters: Context['filters']) => {
  const newFilters = { ...filters };
  const {
    groupDisplayName,
    financialInstitutionId,
    authenticationUserType,
    accessType,
    credentialsType,
    providerName,
    regionName,
  } = newFilters;

  if (providerName) {
    newFilters.providerName = undefined;

    return newFilters;
  }

  if (credentialsType) {
    newFilters.credentialsType = undefined;

    return newFilters;
  }

  if (accessType) {
    newFilters.accessType = undefined;

    return newFilters;
  }

  if (authenticationUserType) {
    newFilters.authenticationUserType = undefined;

    return newFilters;
  }

  if (financialInstitutionId) {
    newFilters.financialInstitutionId = undefined;

    return newFilters;
  }

  if (regionName) {
    newFilters.regionName = undefined;

    return newFilters;
  }

  if (groupDisplayName) {
    newFilters.groupDisplayName = undefined;

    return newFilters;
  }

  return {};
};

type FetchProviderPayload = {
  locale: LocaleCode;
  providerName: string;
};

type FetchProvider = (
  payload: FetchProviderPayload
) => Promise<ResponseObj<ProviderEntity | undefined>>;

type FetchProvidersPayload = {
  market: MarketCode;
  locale: LocaleCode;
};

type FetchProviders = (payload: FetchProvidersPayload) => Promise<ResponseObj<ProvidersList>>;

export type CreateProviderSelectionMachinePayload = {
  market: MarketCode;
  locale: LocaleCode;
  selectedFinancialInstitutionId?: string;
  fallbackToProviderSelection?: boolean;
  enableUnstableProviderWarning: boolean;
  fetchSingleProvider?: FetchProvider;
  fetchProviderList?: FetchProviders;
  fetchDemobankScenarios?: typeof demoBankService;
  fetchRegionMappings?: typeof getRegionMappings;
  initialProviderName?: string;
  initialProviders: ProviderEntity[] | null;
  version: string;
  isDemobankScenariosEnabled?: boolean;
  isRegionSelectionEnabled?: (providerGroupName: string) => boolean;
  productType: ProductType;
};

export const createProviderSelectionMachine = ({
  market,
  locale,
  initialProviderName,
  initialProviders,
  selectedFinancialInstitutionId,
  fallbackToProviderSelection = false,
  enableUnstableProviderWarning,
  fetchSingleProvider = getProvider,
  fetchProviderList = getProviders,
  fetchDemobankScenarios = demoBankService,
  fetchRegionMappings = getRegionMappings,
  version,
  isDemobankScenariosEnabled = false,
  isRegionSelectionEnabled = () => false,
  productType,
}: CreateProviderSelectionMachinePayload) => {
  return createMachine(
    {
      context: {
        error: undefined,
        providerMissing: false,
        providers: initialProviders || [],
        choices: {},
        filters: { financialInstitutionId: selectedFinancialInstitutionId },
        selectedProvider: undefined,
        fetchedProvider: undefined,
        selectedDemobankScenario: undefined,
        demobankScenarios: [],
        regionMappings: {},
      },
      tsTypes: {} as import('./providerSelectionMachine.typegen').Typegen0,
      schema: { context: {} as Context, services: {} as Services, events: {} as Events },
      predictableActionArguments: true,
      id: 'fetch',
      initial: 'decidePreselectedProvider',
      states: {
        decidePreselectedProvider: {
          always: [
            {
              cond: 'hasInitialProviderName',
              target: 'decideFetchSingleProvider',
            },
            {
              target: 'decideFetchingProviders',
            },
          ],
        },
        decideFetchSingleProvider: {
          always: [
            {
              cond: () => Boolean(initialProviders && initialProviderName),
              target: 'findSelectedProvider',
            },
            {
              target: 'fetchSingleProvider',
            },
          ],
        },
        findSelectedProvider: {
          invoke: {
            src: 'findSelectedProvider',
            onDone: [
              {
                actions: 'saveFetchedProvider',
                target: 'fetchProviderDone',
              },
            ],
            onError: [
              {
                actions: 'setProviderNotFound',
                target: 'decideProviderNotFound',
              },
            ],
          },
        },
        decideProviderNotFound: {
          always: [
            {
              cond: 'shouldFallbackToSelection',
              actions: 'setProviderMissing',
              target: 'selectFinancialService',
            },
            {
              actions: 'setProviderNotFound',
              target: 'error',
            },
          ],
        },
        fetchSingleProvider: {
          invoke: {
            src: 'fetchSingleProvider',
            onDone: [
              {
                actions: 'saveFetchedProvider',
                target: 'fetchProviderDone',
              },
            ],
            onError: [
              {
                actions: 'setProviderNotFound',
                target: 'decideProviderNotFound',
              },
            ],
          },
        },
        fetchProviderDone: {
          always: [
            {
              cond: 'shouldFetchAllProviders',
              target: 'fetchProviderList',
            },
            {
              cond: 'providerNotFound',
              actions: 'setProviderNotFound',
              target: 'decideProviderNotFound',
            },
            {
              actions: 'saveSelectedProvider',
              target: 'checkProviderStability',
            },
          ],
        },
        decideFetchingProviders: {
          always: [
            {
              cond: 'hasProviders',
              target: 'fetchProvidersDone',
            },
            {
              target: 'fetchProviderList',
            },
          ],
        },
        fetchProviderList: {
          invoke: {
            src: 'fetchProviderList',
            onDone: [
              {
                actions: 'saveFetchedProviders',
                target: 'fetchProvidersDone',
              },
            ],
            onError: [
              {
                actions: 'setFetchingProvidersFailedError',
                target: 'error',
              },
            ],
          },
        },
        fetchProvidersDone: {
          always: [
            {
              cond: 'providerListIsEmpty',
              target: 'showSelectGroupDisplayNamePicker',
            },
            {
              target: 'selectFinancialService',
            },
          ],
        },
        showDemobankScenarioPicker: {
          on: {
            SELECT_DEMOBANK_SCENARIO: {
              actions: 'saveDemobankScenario',
              target: 'checkProviderStability',
            },
            SELECT_NO_SCENARIO: {
              target: 'selectNoScenario',
            },
            RESET_FILTERS: {
              actions: 'resetFilters',
              target: 'selectGroupDisplayName',
            },
          },
        },
        selectNoScenario: {
          always: [
            {
              target: 'selectAuthenticationType',
            },
          ],
        },
        selectFinancialService: {
          always: [
            {
              cond: 'oneProviderAvailable',
              actions: 'selectOneProvider',
              target: 'checkProviderStability',
            },
            {
              cond: 'shouldShowFinancialServicesPicker',
              target: 'showFinancialServicePicker',
            },
            {
              cond: 'hasPreselectedFinancialInstitutionId',
              target: 'selectCredentialsTypePicker',
            },
            {
              target: 'selectGroupDisplayName',
            },
          ],
        },
        showFinancialServicePicker: {
          on: {
            SELECT_FINANCIAL_SERVICE: {
              actions: 'saveFinancialServiceData',
              target: 'selectCredentialsTypePicker',
            },
          },
        },
        selectGroupDisplayName: {
          always: [
            {
              cond: 'oneProviderAvailable',
              actions: 'selectOneProvider',
              target: 'checkProviderStability',
            },
            {
              target: 'showSelectGroupDisplayNamePicker',
            },
          ],
        },
        showSelectGroupDisplayNamePicker: {
          on: {
            SELECT_GROUP_DISPLAY_NAME: {
              actions: 'saveGroupDisplayName',
              target: 'selectFinancialInstitution',
            },
            SELECT_DISPLAY_NAME: {
              actions: 'saveDisplayName',
              target: 'selectFinancialInstitution',
            },
          },
        },
        fetchDemobankScenarios: {
          invoke: {
            src: 'fetchDemobankScenarios',
            onDone: [
              {
                actions: ['saveFetchedDemobankScenarios', 'saveDemobankProvider'],
                target: 'showDemobankScenarioPicker',
              },
            ],
            onError: [
              {
                actions: 'setFetchingDemobankScenariosFailedError',
                target: 'error',
              },
            ],
          },
        },
        fetchRegionMappings: {
          invoke: {
            src: 'fetchRegionMappings',
            onDone: [
              {
                actions: ['saveFetchedRegionMappings'],
                target: 'showRegionPicker',
              },
            ],
            onError: [
              {
                actions: ['setFetchingRegionMappingError'],
                target: 'error',
              },
            ],
          },
        },
        selectFinancialInstitution: {
          always: [
            {
              cond: 'hasDemobankScenarioSupport',
              target: 'fetchDemobankScenarios',
              actions: 'saveDemobankProvider',
            },
            {
              cond: 'hasRegionSupport',
              target: 'fetchRegionMappings',
            },
            {
              cond: 'oneProviderAvailable',
              actions: 'selectOneProvider',
              target: 'checkProviderStability',
            },
            {
              cond: 'hasMultipleFinancialInstitutions',
              target: 'showFinancialInstitutionPicker',
            },
            {
              target: 'selectAuthenticationType',
            },
          ],
        },
        showRegionPicker: {
          on: {
            SELECT_REGION_PICKER: {
              actions: 'saveSelectedRegion',
              target: 'showFinancialInstitutionPicker',
            },
            RESET_FILTERS: {
              actions: 'resetFilters',
              target: 'selectGroupDisplayName',
            },
          },
        },
        showFinancialInstitutionPicker: {
          on: {
            SELECT_FINANCIAL_INSTITUTION: {
              actions: 'saveFinancialInstitutionId',
              target: 'selectAuthenticationType',
            },
            RESET_FILTERS: {
              actions: 'resetFilters',
              target: 'selectGroupDisplayName',
            },
            SHOW_REGION_PICKER: {
              target: 'showRegionPicker',
              actions: 'resetFilters',
            },
          },
        },
        selectAuthenticationType: {
          always: [
            {
              cond: 'oneProviderAvailable',
              actions: 'selectOneProvider',
              target: 'checkProviderStability',
            },
            {
              cond: 'hasMultipleAuthenticationUserTypes',
              target: 'showSelectAuthenticationUserType',
            },
            {
              target: 'selectAccessType',
            },
          ],
        },
        showSelectAuthenticationUserType: {
          on: {
            SELECT_AUTHENTICATION_USER_TYPE: {
              actions: 'saveAuthenticationUserType',
              target: 'selectAccessType',
            },
            RESET_FILTERS: {
              actions: 'resetFilters',
              target: 'selectGroupDisplayName',
            },
          },
        },
        selectAccessType: {
          always: [
            {
              cond: 'oneProviderAvailable',
              actions: 'selectOneProvider',
              target: 'checkProviderStability',
            },
            {
              cond: 'hasMultipleAccessTypes',
              target: 'showAccessTypePicker',
            },
            {
              target: 'selectCredentialsTypePicker',
            },
          ],
        },
        showAccessTypePicker: {
          on: {
            SELECT_ACCESS_TYPE: {
              actions: 'saveAccessType',
              target: 'selectCredentialsTypePicker',
            },
            RESET_FILTERS: {
              actions: 'resetFilters',
              target: 'selectGroupDisplayName',
            },
          },
        },
        selectCredentialsTypePicker: {
          always: [
            {
              cond: 'oneProviderAvailable',
              actions: 'selectOneProvider',
              target: 'checkProviderStability',
            },
            {
              cond: 'hasMultipleCredentialsTypes',
              target: 'showCredentialsTypePicker',
            },
            {
              target: 'selectDisplayDescription',
            },
          ],
        },
        showCredentialsTypePicker: {
          on: {
            SELECT_CREDENTIALS_TYPE: {
              actions: 'saveCredentialsType',
              target: 'selectDisplayDescription',
            },
            RESET_FILTERS: {
              actions: 'resetFilters',
              target: 'selectGroupDisplayName',
            },
            RESET_FINANCIAL_SERVICES_DATA: {
              actions: 'resetFinancialServicesData',
              target: 'selectFinancialService',
            },
          },
        },
        selectDisplayDescription: {
          always: [
            {
              cond: 'oneProviderAvailable',
              actions: 'selectOneProvider',
              target: 'checkProviderStability',
            },
            {
              cond: 'hasMultipleDisplayDescriptions',
              target: 'showDisplayDescriptionPicker',
            },
          ],
        },
        showDisplayDescriptionPicker: {
          on: {
            SELECT_PROVIDER_NAME: {
              actions: ['saveProviderName', 'selectOneProvider'],
              target: 'checkProviderStability',
            },
            RESET_FILTERS: {
              actions: 'resetFilters',
              target: 'selectGroupDisplayName',
            },
          },
        },
        checkProviderStability: {
          always: [
            {
              cond: 'isProviderUnstable',
              target: 'showProviderUnstableScreen',
            },
            {
              target: 'done',
            },
          ],
        },
        showProviderUnstableScreen: {
          on: {
            UNSTABLE_PROVIDER_ACKNOWLEDGED: {
              target: 'done',
            },
            RESET_FILTERS: {
              actions: 'resetFilters',
              target: 'selectGroupDisplayName',
            },
            USER_CANCELLED: {
              actions: 'setUserCancelledError',
              target: 'error',
            },
          },
        },
        done: {
          type: 'final',
        },
        error: {
          type: 'final',
        },
      },
    },
    {
      services: {
        findSelectedProvider: async (ctx) => {
          const [errorMessage, provider] = findProvider(
            ctx.providers,
            initialProviderName!,
            version
          );

          if (errorMessage || !provider) {
            throw new Error(errorMessage);
          }

          return provider;
        },
        fetchSingleProvider: async () => {
          const response = await fetchSingleProvider({
            providerName: initialProviderName!,
            locale,
          });

          return response.data;
        },
        fetchProviderList: async () => {
          const response = await fetchProviderList({
            market,
            locale,
          });

          return response.data.providers;
        },
        fetchDemobankScenarios: async () => {
          const response = await fetchDemobankScenarios.getUsers({
            userProduct: productType,
            userMarket: market,
          });

          return response;
        },
        fetchRegionMappings: async () => {
          const response = await fetchRegionMappings();

          return response;
        },
      },
      guards: {
        shouldFetchAllProviders: (context) => {
          const { fetchedProvider } = context;

          return (
            fetchedProvider === undefined &&
            Boolean(initialProviderName) &&
            fallbackToProviderSelection
          );
        },
        isProviderUnstable: (ctx) => {
          if (!enableUnstableProviderWarning) {
            return false;
          }

          const { selectedProvider: provider } = ctx;
          const providerUnstable =
            Provider.isDown(provider!) ||
            Provider.isTemporaryDisabled(provider!) ||
            Provider.isUnhealthy(provider!);

          return providerUnstable;
        },
        hasInitialProviderName: () => Boolean(initialProviderName),
        oneProviderAvailable: (ctx) => {
          return getFilteredProviders(ctx).length === 1;
        },
        hasPreselectedFinancialInstitutionId: () => {
          return Boolean(selectedFinancialInstitutionId);
        },
        hasProviders: () => Boolean(initialProviders),
        providerListIsEmpty: (context) => {
          return context.providers.length === 0;
        },
        providerNotFound: (context) => {
          return context.fetchedProvider === undefined;
        },
        shouldShowFinancialServicesPicker: (ctx) => {
          const providers = getFilteredProviders(ctx);
          const uniqueFinancialServices = getUniqueFinancialServices(providers);

          return Boolean(selectedFinancialInstitutionId) && uniqueFinancialServices.length > 1;
        },
        hasMultipleFinancialInstitutions: (ctx) => {
          const providers = getFilteredProviders(ctx);
          const providerFinancialInstitutions = groupByFinancialInstitution(providers);

          return Object.keys(providerFinancialInstitutions).length > 1;
        },
        hasDemobankScenarioSupport: (ctx) => {
          const hasSelectedDemobank = ctx.filters.groupDisplayName === 'Tink Demo Bank';
          const hasEmbeddedDemobankProvider = hasDemobankProviders(ctx.providers);

          return isDemobankScenariosEnabled && hasSelectedDemobank && hasEmbeddedDemobankProvider;
        },
        hasRegionSupport: (ctx) => {
          const { groupDisplayName } = ctx.filters;

          return isRegionSelectionEnabled(groupDisplayName || '');
        },
        hasMultipleAuthenticationUserTypes: (ctx) => {
          const providers = getFilteredProviders(ctx);
          const providerAuthenticationUserTypes = groupByAuthenticationUserType(providers);

          return Object.keys(providerAuthenticationUserTypes).length > 1;
        },
        hasMultipleAccessTypes: (ctx) => {
          const providers = getFilteredProviders(ctx);
          const providerAccessTypes = groupByAccessType(providers);

          return Object.keys(providerAccessTypes).length > 1;
        },
        hasMultipleCredentialsTypes: (ctx) => {
          const providers = getFilteredProviders(ctx);
          const providerCredentialsTypes = groupByCredentialsType(providers);

          return Object.keys(providerCredentialsTypes).length > 1;
        },
        hasMultipleDisplayDescriptions: (ctx) => {
          const providers = getFilteredProviders(ctx);
          const providerDisplayDescriptionGroups = groupByDisplayDescription(providers);
          const providerDisplayDescriptions = Object.keys(providerDisplayDescriptionGroups);

          // No displayDescriptions to render
          if (providerDisplayDescriptions.length === 0) {
            return false;
          }

          if (providerDisplayDescriptions.length > 1) {
            return true;
          }

          // Even after all the filtering, more than providers have been grouped under 1 displayDescription.
          // This is sometimes the case between *-ob and *-fallback provider (e.g. se-swedbank-ob and se-swedbank-fallback)
          // Since there is no identifiable information to render for user to inform their choice, we just render
          // display description twice and hope for the best.
          // Since we already checked the case for  1 provider only, we can be sure that there are multiple providers.
          return true;
        },
        shouldFallbackToSelection: () => fallbackToProviderSelection,
      },
      actions: {
        saveSelectedProvider: assign({
          selectedProvider: (context) => context.fetchedProvider,
        }),
        saveFetchedProvider: assign({
          fetchedProvider: (_, event) => event.data,
        }),
        saveFetchedProviders: assign({
          providers: (_, event) => {
            return event.data;
          },
        }),
        selectOneProvider: assign((ctx) => {
          return {
            selectedProvider: getFilteredProviders(ctx)[0],
          };
        }),
        setFetchingProvidersFailedError: assign({
          error: (_) => ERROR_PROVIDERS,
        }),
        setProviderNotFound: assign({
          error: (_) => ERR_PROVIDER_NOT_FOUND,
        }),
        setUserCancelledError: assign({
          error: (_) => ERR_USER_CANCELLED,
        }),
        setFetchingDemobankScenariosFailedError: assign({
          error: (_) => ERR_FETCH_DEMOBANK_SCENARIOS_FAILED,
        }),
        setFetchingRegionMappingError: assign({
          error: (_) => ERR_FETCH_REGION_MAPPING,
        }),
        saveFetchedRegionMappings: assign({
          regionMappings: (_, event) => {
            return event.data;
          },
        }),
        saveSelectedRegion: assign((ctx, event) => {
          return {
            filters: {
              ...ctx.filters,
              regionName: event.regionName,
            },
          };
        }),
        saveGroupDisplayName: assign((ctx, event) => {
          return {
            filters: {
              ...ctx.filters,
              groupDisplayName: event.groupDisplayName,
            },
          };
        }),
        resetFilters: assign((ctx) => {
          const newFilters = resetFilters(ctx.filters);

          return {
            ...ctx,
            filters: newFilters,
          };
        }),
        setProviderMissing: assign({
          providerMissing: (_) => true,
        }),
        saveFinancialInstitutionId: assign((ctx, event) => {
          return {
            filters: {
              ...ctx.filters,
              financialInstitutionId: event.financialInstitutionId,
            },
          };
        }),
        saveAuthenticationUserType: assign({
          filters: (ctx, event) => ({
            ...ctx.filters,
            authenticationUserType: event.authenticationUserType,
          }),
        }),
        saveAccessType: assign({
          filters: (ctx, event) => ({
            ...ctx.filters,
            accessType: event.accessType,
          }),
        }),
        saveCredentialsType: assign({
          filters: (ctx, event) => ({
            ...ctx.filters,
            credentialsType: event.credentialsType,
          }),
        }),
        saveFinancialServiceData: assign({
          filters: (ctx, event) => ({
            ...ctx.filters,
            accessType: event.accessType,
            authenticationUserType:
              event.financialServicesSegment as unknown as AuthenticationUserType,
          }),
        }),
        resetFinancialServicesData: assign({
          filters: (ctx) => ({
            ...ctx.filters,
            accessType: undefined,
            authenticationUserType: undefined,
          }),
        }),
        saveProviderName: assign({
          filters: (ctx, event) => ({
            ...ctx.filters,
            providerName: event.providerName,
          }),
        }),
        saveDisplayName: assign({
          filters: (ctx, event) => ({
            ...ctx.filters,
            displayName: event.displayName,
          }),
        }),
        saveFetchedDemobankScenarios: assign({
          demobankScenarios: (_, event) => {
            return event.data;
          },
        }),
        saveDemobankProvider: assign((ctx) => ({
          ...ctx,
          selectedProvider: findDemobankProviders(ctx.providers),
        })),
        saveDemobankScenario: assign((ctx, event) => ({
          ...ctx,
          selectedDemobankScenario: ctx.demobankScenarios.find((s) => s.id === event.scenarioId),
        })),
      },
    }
  );
};
